import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { BadgeCheck, CheckCircle2, MapPin } from 'lucide-react';
import { PageSEO } from '../../components/seo/SchemaMarkup';
import { API, Eyebrow, LocatorLoading, therapyLabel, trackLocator } from './locatorShared';

const inputCls = 'w-full bg-zinc-900 border border-zinc-700 focus:border-[#C7FA3E] outline-none text-white text-sm px-3 py-3 placeholder:text-zinc-600';

export default function LocatorClaim() {
  const { slug } = useParams();
  useEffect(() => { trackLocator('claim_view', { slug }); }, [slug]);
  const [facility, setFacility] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', role: '', phone: '', message: '' });
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    window.scrollTo(0, 0);
    fetch(`${API}/api/locator/facility/${slug}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setFacility)
      .catch(() => setNotFound(true));
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const res = await fetch(`${API}/api/locator/facility/${slug}/claim`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Claim failed');
      setDone(true);
    } catch (err) {
      setError(typeof err.message === 'string' ? err.message : 'Claim failed — please retry.');
    }
  };

  if (notFound) {
    return (
      <div className="min-h-[60vh] bg-black flex items-center justify-center px-6">
        <div className="text-center">
          <h1 className="text-white text-3xl mb-3" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Listing Not Found</h1>
          <Link to="/locator" className="text-[#C7FA3E] text-sm underline underline-offset-2">Browse the recovery locator</Link>
        </div>
      </div>
    );
  }
  if (!facility) return <LocatorLoading />;

  return (
    <div className="bg-black min-h-screen px-6 pt-28 pb-16" data-testid="locator-claim-page">
      <PageSEO
        title={`Claim ${facility.name} — Free Listing Upgrade | NinjAthlete`}
        description={`Claim the ${facility.name} listing in the NinjAthlete Recovery Locator to add photos, pricing, staff and a booking link — free.`}
        path={`/locator/claim/${slug}`}
      />
      <div className="max-w-xl mx-auto">
        <Eyebrow>Claim Your Listing — Free</Eyebrow>
        <h1 className="text-white text-4xl sm:text-5xl leading-tight mb-4" style={{ fontFamily: 'Bebas Neue, sans-serif' }} data-testid="claim-h1">
          Claim {facility.name}
        </h1>

        <div className="border border-zinc-800 bg-zinc-900/60 p-4 mb-6">
          <p className="text-white text-sm font-medium">{facility.name}</p>
          <p className="text-zinc-500 text-xs flex items-center gap-1.5 mt-1"><MapPin className="w-3 h-3" /> {facility.city}{facility.state ? `, ${facility.state}` : ''}</p>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {(facility.therapies || []).slice(0, 8).map((t) => (
              <span key={t} className="px-2 py-0.5 text-[10px] uppercase tracking-wider bg-zinc-800 text-zinc-400">{therapyLabel(t)}</span>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-8 text-zinc-400 text-xs">
          {['Photo gallery + logo', 'Service menu with pricing', 'Staff cards with Instagram', 'Tracked booking link', 'Intro offer / promo code', 'Hours & schedule'].map((perk) => (
            <p key={perk} className="flex items-center gap-2"><BadgeCheck className="w-3.5 h-3.5 text-[#C7FA3E] flex-shrink-0" /> {perk}</p>
          ))}
        </div>

        {done ? (
          <div className="border border-[#C7FA3E]/30 bg-[#C7FA3E]/[0.05] p-8 text-center" data-testid="claim-success">
            <CheckCircle2 className="w-10 h-10 text-[#C7FA3E] mx-auto mb-4" />
            <h2 className="text-white text-2xl mb-2" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Claim Received</h2>
            <p className="text-zinc-400 text-sm">We verify ownership and reply within 2 business days with next steps for upgrading your listing.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <input required value={form.name} onChange={set('name')} className={inputCls} placeholder="Your name *" data-testid="claim-name" />
              <input value={form.role} onChange={set('role')} className={inputCls} placeholder="Role (Owner, GM…)" data-testid="claim-role" />
            </div>
            <input required type="email" value={form.email} onChange={set('email')} className={inputCls} placeholder="Work email (matches your facility domain ideally) *" data-testid="claim-email" />
            <input value={form.phone} onChange={set('phone')} className={inputCls} placeholder="Phone" data-testid="claim-phone" />
            <textarea value={form.message} onChange={set('message')} rows={3} className={inputCls} placeholder="Anything to add or correct on the listing?" data-testid="claim-message" />
            <button type="submit" className="w-full py-3.5 bg-[#C7FA3E] text-black text-xs font-bold uppercase tracking-widest hover:opacity-90 transition-opacity" style={{ fontFamily: 'Oswald, sans-serif' }} data-testid="claim-submit-btn">
              Claim This Listing
            </button>
            {error && <p className="text-[#E63946] text-sm" data-testid="claim-error">{error}</p>}
          </form>
        )}
      </div>
    </div>
  );
}
